import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import MobileMenuDropdown from './MobileMenuDropdown';
import AccountDropdown from '../shared/AccountDropdown';
import { getDashboardBackTarget } from '../../utils/dashboardMobileNav';

import { MOBILE_BOTTOM_NAV_CLEARANCE } from '../../constants/mobileBottomNav';

const MOBILE_CONTENT_PADDING = `12px 12px ${MOBILE_BOTTOM_NAV_CLEARANCE}`;
/** Bottom inset for bottom nav; no side padding — lists flush to screen edges. */
const MOBILE_CONTENT_PADDING_FLUSH = `0 0 ${MOBILE_BOTTOM_NAV_CLEARANCE}`;

/**
 * Mobile shell for dashboard routes that are not bottom-nav tabs.
 * Title row with back chevron + hamburger menu (sidebar parity).
 *
 * @param {boolean} [flushContent] When true, content has no horizontal padding
 *   (use for edge-to-edge lists with their own cell padding).
 */
export default function DashboardMobileSubPageLayout({
  title,
  children,
  backTo,
  showAccountDropdown = false,
  showBack = true,
  flushContent = false,
}) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    navigate(backTo ?? getDashboardBackTarget(location.pathname, location.state));
  };

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 'env(safe-area-inset-top)',
        backgroundColor: colors.bgPrimary,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px',
          borderBottom: `1px solid ${colors.borderLight}`,
          flexShrink: 0,
        }}
      >
        {showBack && (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            style={{
              padding: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              backgroundColor: colors.bgHeader,
              border: 'none',
              color: colors.text,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <h5
          style={{
            margin: 0,
            color: colors.text,
            fontSize: '1.1rem',
            fontWeight: 600,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </h5>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {showAccountDropdown && (
            <AccountDropdown compact labelTailChars={5} sizeToContent />
          )}
          <MobileMenuDropdown currentPage="dashboard" />
        </div>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: flushContent ? MOBILE_CONTENT_PADDING_FLUSH : MOBILE_CONTENT_PADDING,
        }}
      >
        {children}
      </div>
    </div>
  );
}
